var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BeoButton } from "@components/common/action";
import { BeoPassword } from "@components/common/input";
import { BeoDialogue } from "@components/common/view";
import { ResetPasswordApi } from "@services/user/UserService";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
var SUCCESS_MESSAGE = "Password reset successful";
var INVALID_TOKEN_MESSAGE = "Invalid password reset link.";
var PASSWORD_MISMATCH_MESSAGE = "Passwords do not match.";
var GENERIC_RESET_ERROR_MESSAGE = "Unable to reset password.";
/**
 * ResetPassword Component
 *
 * Renders the password-reset form for users who access the application
 * through a password-reset link containing a reset token.
 *
 * Features:
 * - Retrieves the password-reset token from the URL query parameters.
 * - Uses react-hook-form for form handling and validation.
 * - Validates that both password fields are provided.
 * - Verifies that the new password and confirmation password match.
 * - Calls ResetPasswordApi to update the user's password.
 * - Displays success and error messages in a BeoDialogue.
 * - Redirects the user to the login page after a successful password reset.
 * - Uses `autoComplete="new-password"` for password fields to indicate that
 *   a new password is being created rather than using an existing saved password.
 * - Allows the user to return to the login page using the Back to Login button.
 *
 * @component
 * @author sachin.simon
 * @example
 * return (
 *   <ResetPassword />
 * );
 */
var ResetPassword = function () {
    var methods = useForm({
        mode: "onSubmit",
    });
    var control = methods.control, handleSubmit = methods.handleSubmit, errors = methods.formState.errors;
    var navigate = useNavigate();
    var searchParams = useSearchParams()[0];
    var _a = useState(""), message = _a[0], setMessage = _a[1];
    var _b = useState(false), dialoguePupup = _b[0], setDialoguePupup = _b[1];
    var _c = useState(false), resetSuccessful = _c[0], setResetSuccessful = _c[1];
    /**
     * Handles submission of the reset-password form.
     *
     * Validates the reset token and verifies that the password and confirmation
     * password match before sending the reset request to the backend.
     *
     * On success, displays the success dialogue and marks the reset operation
     * as successful so that the user is redirected to the login page after
     * closing the dialogue.
     *
     * @param data - Form values containing the new password and confirmation password.
     */
    var handleResetPassword = function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var token, request;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    token = searchParams.get("token");
                    if (!token) {
                        setMessage(INVALID_TOKEN_MESSAGE);
                        setResetSuccessful(false);
                        setDialoguePupup(true);
                        return [2 /*return*/];
                    }
                    if (data.password !== data.confirmPassword) {
                        setMessage(PASSWORD_MISMATCH_MESSAGE);
                        setResetSuccessful(false);
                        setDialoguePupup(true);
                        return [2 /*return*/];
                    }
                    request = {
                        token: token,
                        newPassword: data.password,
                    };
                    return [4 /*yield*/, ResetPasswordApi(request, {
                            onSuccess: function (response) {
                                if ((response === null || response === void 0 ? void 0 : response.responseStatus) === "SUCCESS") {
                                    setMessage(response.responseBody || SUCCESS_MESSAGE);
                                    setResetSuccessful(true);
                                    setDialoguePupup(true);
                                }
                            },
                            onError: function (error) {
                                setMessage((error === null || error === void 0 ? void 0 : error.message) || GENERIC_RESET_ERROR_MESSAGE);
                                setResetSuccessful(false);
                                setDialoguePupup(true);
                            },
                        })];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    /**
     * Closes the reset-password result dialogue.
     *
     * If the password reset was successful, the redirect effect will navigate
     * the user to the login page after the dialogue is closed.
     */
    var handleDialogueClose = function () {
        setDialoguePupup(false);
    };
    /**
     * Redirects the user to the login page after a successful password reset
     * once the result dialogue has been closed.
     */
    useEffect(function () {
        if (!dialoguePupup && resetSuccessful) {
            navigate("/login");
        }
    }, [dialoguePupup, resetSuccessful, navigate]);
    /**
     * Navigates the user back to the login page.
     */
    var handleBackToLogin = function () {
        navigate("/login");
    };
    return (_jsxs(_Fragment, { children: [_jsxs(BeoDialogue, { id: "alert-box", header: "Alert", visible: dialoguePupup, style: { width: "20vw" }, onHide: handleDialogueClose, modal: true, children: [_jsxs("div", { className: "flex flex-col items-center space-y-4 text-center", children: [_jsx("div", { className: "text-red-500 text-2xl", children: "\u26A0\uFE0F" }), _jsx("p", { className: "text-gray-700 font-medium", children: message })] }), _jsx("div", { className: "flex justify-center mt-6", children: _jsx(BeoButton, { id: "OK_button", label: "OK", onClick: handleDialogueClose, className: "!bg-red-600 !border-none !rounded-lg px-6 py-2 !shadow-md hover:!bg-red-500" }) })] }), _jsx(FormProvider, __assign({}, methods, { children: _jsx("div", { className: "min-h-screen flex items-center justify-center login-page", children: _jsxs("section", { className: "w-full max-w-md px-4 py-8", children: [_jsx("div", { className: "beo-logo mx-auto mb-8" }), _jsx("main", { className: "backdrop-blur-xl bg-white/75 rounded-xl shadow-2xl p-10 w-full", children: _jsx("div", { className: "space-y-6", children: _jsxs("form", { onSubmit: handleSubmit(handleResetPassword), className: "space-y-4", children: [_jsx("div", { className: "eakz-logo" }), _jsx("h2", { className: "text-center text-2xl font-bold text-gray-900", children: "Reset Password" }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { htmlFor: "password", className: "block mb-1 text-sm text-gray-900", children: "New Password" }), _jsx(Controller, { name: "password", control: control, rules: {
                                                            required: "New Password is required",
                                                        }, render: function (_a) {
                                                            var field = _a.field;
                                                            return (_jsx(BeoPassword, { id: "password", placeholder: "Enter your new password", value: field.value || "", onChange: field.onChange, onBlur: field.onBlur, autoComplete: "new-password", className: "w-full rounded-md border bg-gray-50 ".concat(errors.password
                                                                    ? "border-red-500"
                                                                    : "border-gray-300") }));
                                                        } }), errors.password && (_jsx("div", { className: "text-red-500 text-[11px] mt-1", children: errors.password.message }))] }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { htmlFor: "confirmPassword", className: "block mb-1 text-sm text-gray-900", children: "Confirm Password" }), _jsx(Controller, { name: "confirmPassword", control: control, rules: {
                                                            required: "Confirm Password is required",
                                                        }, render: function (_a) {
                                                            var field = _a.field;
                                                            return (_jsx(BeoPassword, { id: "confirmPassword", placeholder: "Confirm your new password", value: field.value || "", onChange: field.onChange, onBlur: field.onBlur, autoComplete: "new-password", className: "w-full rounded-md border bg-gray-50 ".concat(errors.confirmPassword
                                                                    ? "border-red-500"
                                                                    : "border-gray-300") }));
                                                        } }), errors.confirmPassword && (_jsx("div", { className: "text-red-500 text-[11px] mt-1", children: errors.confirmPassword.message }))] }), _jsxs("div", { className: "grid grid-cols-2 gap-2 pt-2", children: [_jsx(BeoButton, { id: "submit", label: "Submit", type: "submit", className: "w-full py-3 px-4 flex items-center justify-center text-lg font-bold rounded-md text-gray-900 bg-gradient-to-r from-yellow-300 to-orange-400 hover:from-yellow-400 hover:to-orange-500 transition-all duration-300 transform hover:scale-[1.02] shadow-lg" }), _jsx(BeoButton, { id: "back-to-login", label: "Back to Login", type: "button", onClick: handleBackToLogin, className: "w-full py-3 px-4 flex items-center justify-center text-lg font-bold rounded-md text-gray-900 bg-gradient-to-r from-yellow-300 to-orange-400 hover:from-yellow-400 hover:to-orange-500 transition-all duration-300 transform hover:scale-[1.02] shadow-lg" })] })] }) }) })] }) }) }))] }));
};
export default ResetPassword;
